import * as Location from 'expo-location';
import {
  getForegroundPermissionsAsync,
  requestForegroundPermissionsAsync
} from 'expo-location';
import type { FC, ReactElement } from 'react';
import { cloneElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { PressableProps } from 'react-native-gesture-handler';

import { usePopup } from '@/features/popup/provider/usePopup';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { openAppSettings } from '@/shared/utils/openSettings';
import { isWeb } from '@/shared/utils/platform';

type ChildWithOnPress = ReactElement<
  Pick<PressableProps, 'onPress' | 'disabled'>
>;

type LocationGuardProps = {
  children: ChildWithOnPress;
};

/** A component that wraps a child element and ensures location services and permissions
 *  are granted before allowing the child element's onPress event to be triggered.
 * @param children
 *
 * Usage:
 * <LocationGuard>
 *   <Button onPress={handlePress} title="Get Location" />
 * </LocationGuard>
 *
 * This will check for location services and permissions before calling handlePress.
 */
export const LocationGuard: FC<LocationGuardProps> = ({ children }) => {
  const { t } = useTranslation(LocalizationNameSpace.SHARED, {
    keyPrefix: 'location'
  });
  const { showAlert } = usePopup();

  const showServicesDisabledAlert = () =>
    showAlert({
      dismissable: true,
      dismissOnConfirm: true,
      labels: t('noLocationService', { returnObjects: true })
    });

  const showPermissionDeniedAlert = () =>
    showAlert({
      dismissable: true,
      dismissOnConfirm: true,
      onConfirm: isWeb ? undefined : openAppSettings,
      labels: t('noLocationPermission', { returnObjects: true })
    });

  const originalOnPress = children.props.onPress;
  const handlePress: PressableProps['onPress'] = async (event) => {
    try {
      if (!isWeb) {
        const servicesEnabled = await Location.hasServicesEnabledAsync();
        if (!servicesEnabled) {
          showServicesDisabledAlert();
          return;
        }
      }

      const currentStatus = await getForegroundPermissionsAsync();

      if (currentStatus.granted) {
        originalOnPress?.(event);
        return;
      }

      const isPermanentlyDenied = isWeb
        ? currentStatus.status === 'denied'
        : !currentStatus.canAskAgain && currentStatus.status === 'denied';

      if (isPermanentlyDenied) {
        showPermissionDeniedAlert();
        return;
      }

      const result = await requestForegroundPermissionsAsync();
      if (result.granted) {
        originalOnPress?.(event);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Error in LocationGuard:', error.message);
      }
    }
  };

  return cloneElement(children, {
    onPress: handlePress
  });
};
